import classNames from 'classnames'

import { useGetUnitTrafficInfinite } from '@apps-common/apis/portal/unit'
import { UnitContentCard } from '@apps-common/apps/portal/pages/units/pages/manage/components'
import { EventItemCard, EventItemCardSkeleton } from '@apps-common/apps/portal/pages/units/pages/manage/pages/events/components'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundArrow } from '@apps-common/components/elements/button'
import { commonIcons } from '@apps-common/config/icons'
import { useCustomWindowDimensions } from '@apps-common/hooks/useCustomWindowDimensions'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { IUnitGenericDetailsModel, IUnitTrafficModel } from '@apps-common/types/portal/unit'
import { ROUTE_UNIT_EVENTS } from '@apps-radionet-portal/config/private-routes'
import { useMemo } from 'react'

const TAKE = 2

interface IProps {
  parentLoading: boolean
  unit?: IUnitGenericDetailsModel
}

export const EventsCard: React.FC<IProps> = ({ parentLoading, unit }) => {
  const { navigateForward } = useNavigation()
  const { status, unitId, ipAddress, imei } = unit || {}

  const { value } = useCustomWindowDimensions({
    sizes: [
      {
        width: 1565,
        value: 6
      },
      {
        width: 1024,
        value: 2
      },
      {
        width: 0,
        value: 1
      }
    ],
    defaultSize: {
      width: 0,
      value: TAKE
    }
  })

  const queryData = useMemo(() => {
    if (!unit || parentLoading) {
      return null
    }

    return {
      take: value,
      identifier: `${ipAddress},${imei}`
    }
  }, [value, unit])

  const { isLoading, isRefetching, isError, data } = useGetUnitTrafficInfinite({
    config: {
      enabled: !!queryData
    },
    identifiers: queryData?.identifier || '',
    take: queryData?.take || 0
  })

  const navigateEvents = () => navigateForward({ routeUrl: ROUTE_UNIT_EVENTS.buildPath(status, unitId), route: ROUTE_UNIT_EVENTS })

  const events = data?.pages?.reduce((acc: IUnitTrafficModel[], curr: IUnitTrafficModel[]) => [...acc, ...curr], []) || []

  const loading = !unit || parentLoading || !data || isLoading || isRefetching || isError

  return (
    <UnitContentCard classes='relative overflow-hidden h-full'>
      <div className='relative z-[1] flex flex-col gap-[15px] h-full'>
        <div className='flex items-center justify-between'>
          <SectionTitle classes='text-text' text='Events' />
          <ButtonRoundArrow classes='bg-brand hover:!bg-brand' iconClasses='text-on-brand' callback={navigateEvents} />
        </div>
        <div className='grid gap-[10px] grow'>
          {loading && Array.from(Array(queryData?.take).keys()).map((k) => <EventItemCardSkeleton key={`unit-event-item-skeleton-${k}`} />)}
          {!loading && events.length === 0 && (
            <div className='bg-surface-2 text-text jost-light flex items-center justify-center rounded-[35px] grow'>
              <div className='bg-surface p-2'>No recent events</div>
            </div>
          )}
          {!loading && events.map((e: IUnitTrafficModel) => <EventItemCard key={`unit-event-item-${e.id}`} event={e} />)}
        </div>

        <div className={classNames('flex items-center justify-center', (loading || events.length > 0) && 'grow')}>
          {loading && (
            <div className='mx-auto bg-surface-2 text-text !text-[0.8rem] !h-auto !max-h-[35px] !min-h-[24px] !min-w-[110px] !px-[20px] rounded-full flex items-center justify-between gap-[5px]' />
          )}
          {!loading && (
            <button
              type='button'
              className='mx-auto bg-surface-2 text-text sm:bg-surface-2 sm:text-text !text-[0.8rem] !h-auto !max-h-[35px] py-1.5 !min-w-0 !px-[20px] rounded-full flex items-center justify-between gap-[5px]'
              onClick={navigateEvents}
            >
              More events <commonIcons.arrowAngled className='text-[1.1em]' />
            </button>
          )}
        </div>
      </div>
    </UnitContentCard>
  )
}
